"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AutosuggestBase = void 0;
const react_1 = __importStar(require("react"));
const TextField_1 = __importDefault(require("@material-ui/core/TextField"));
const Autocomplete_1 = __importDefault(require("@material-ui/lab/Autocomplete"));
const Link_1 = __importDefault(require("@material-ui/core/Link"));
const Search_1 = __importDefault(require("@material-ui/icons/Search"));
const InputAdornment_1 = __importDefault(require("@material-ui/core/InputAdornment"));
const styles_1 = require("@material-ui/core/styles");
const TokenGroup_1 = __importDefault(require("../TokenGroup"));
const Checkbox_1 = __importDefault(require("../Checkbox"));
const Stack_1 = __importDefault(require("../../layouts/Stack"));
const LoadingIndicator_1 = __importDefault(require("../LoadingIndicator"));
const StatusIndicator_1 = __importDefault(require("../StatusIndicator"));
const getFlattenOptions_1 = require("../../utils/getFlattenOptions");
const useUniqueId_1 = __importDefault(require("../../hooks/useUniqueId"));
const useStyles = styles_1.makeStyles({
    input: {
        display: 'inline-block',
        marginRight: '20px',
    },
    recoveryLink: {
        pointerEvents: 'auto',
    },
    textfield: {
        paddingRight: '30px',
    },
});
const covertStringValue = (value) => {
    if (typeof value === 'string') {
        return {
            value,
            label: value,
        };
    }
    return value;
};
const getValue = (value) => {
    if (!value.value) {
        return value.multiple ? [] : null;
    }
    if (value.multiple) {
        return value.value.map(covertStringValue);
    }
    return covertStringValue(value.value);
};
/**
 * An autosuggest control is a normal text input enhanced by a panel of suggested options.
 * */
const AutosuggestBase = (_a) => {
    var { options = [], filteringType = 'auto', loadingText = 'Loading ...', errorText = 'Error fetching', recoveryText, name, empty, disabled, placeholder, invalid, icon, ariaRequired = false, freeSolo = false, disableClearable = false, ariaDescribedby, ariaLabelledby, onInputChange, onFocus, onBlur, onRecoveryClick, disableBrowserAutocorrect = false, statusType = 'finished', filterOptions, value } = _a, props = __rest(_a, ["options", "filteringType", "loadingText", "errorText", "recoveryText", "name", "empty", "disabled", "placeholder", "invalid", "icon", "ariaRequired", "freeSolo", "disableClearable", "ariaDescribedby", "ariaLabelledby", "onInputChange", "onFocus", "onBlur", "onRecoveryClick", "disableBrowserAutocorrect", "statusType", "filterOptions", "value"]);
    const classes = useStyles();
    const [inputValue, setInputValue] = react_1.useState(getValue({
        multiple: props.multiple,
        value,
    }));
    const [open, setOpen] = react_1.default.useState(false);
    const controlId = useUniqueId_1.default(props.controlId);
    const autoCompleteString = disableBrowserAutocorrect ? 'off' : 'on';
    react_1.useEffect(() => {
        setInputValue(getValue({
            multiple: props.multiple,
            value,
        }));
    }, [props.multiple, value, setInputValue]);
    const onRecoveryClickHandler = react_1.useCallback((event) => {
        event.preventDefault();
        onRecoveryClick === null || onRecoveryClick === void 0 ? void 0 : onRecoveryClick(event);
        event.stopPropagation();
    }, [onRecoveryClick]);
    const flattenOptions = react_1.useMemo(() => {
        return getFlattenOptions_1.getFlattenOptions(options);
    }, [options]);
    const handleOnChange = react_1.useCallback((_, value) => {
        var _a;
        const newValue = getValue({
            multiple: props.multiple,
            value,
        });
        setInputValue(newValue);
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, newValue);
    }, [props, setInputValue]);
    const handleOnInput = react_1.useCallback((event, value, reason) => {
        if (filteringType === 'manual') {
            onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(event, value, reason);
        }
    }, [onInputChange, filteringType]);
    const getOptionsSelected = react_1.useCallback((option, value) => {
        return option.value === value.value;
    }, []);
    const loadingAndErrorText = react_1.useMemo(() => (react_1.default.createElement(react_1.default.Fragment, null,
        statusType === 'loading' && react_1.default.createElement(LoadingIndicator_1.default, { label: loadingText }),
        statusType === 'error' && (react_1.default.createElement(StatusIndicator_1.default, { statusType: "negative" },
            errorText,
            ".",
            recoveryText && (react_1.default.createElement("span", null,
                react_1.default.createElement(Link_1.default, { href: "#", onClick: onRecoveryClickHandler, className: classes.recoveryLink }, recoveryText))))))), [statusType, classes.recoveryLink, errorText, recoveryText, loadingText, onRecoveryClickHandler]);
    const iconComponent = react_1.useMemo(() => {
        if (icon) {
            const IconComponent = icon;
            return react_1.default.createElement(IconComponent, { color: "action" });
        }
        return react_1.default.createElement(Search_1.default, { color: "action" });
    }, [icon]);
    const textfield = react_1.useCallback((params) => (react_1.default.createElement(TextField_1.default, Object.assign({ autoCorrect: autoCompleteString, placeholder: placeholder, required: ariaRequired, error: !!(statusType === 'error' || invalid) }, params, { variant: "outlined", size: "small", margin: "normal", name: name, InputProps: Object.assign(Object.assign({ 'aria-required': ariaRequired, 'aria-describedby': ariaDescribedby, 'aria-labelledby': ariaLabelledby }, params.InputProps), { className: classes.textfield, type: 'search', startAdornment: icon !== false && react_1.default.createElement(InputAdornment_1.default, { position: "start" }, iconComponent) }) }))), [
        ariaDescribedby,
        ariaLabelledby,
        ariaRequired,
        autoCompleteString,
        classes.textfield,
        invalid,
        name,
        placeholder,
        statusType,
        icon,
        iconComponent,
    ]);
    const renderOption = react_1.useCallback((option) => {
        var _a, _b;
        if (props.multiple && props.checkboxes) {
            return (react_1.default.createElement(Checkbox_1.default, { checked: inputValue.map((input) => input.value).includes(option.value), value: option.value }, ((_a = props.renderOption) === null || _a === void 0 ? void 0 : _a.call(props, option)) || option.label));
        }
        return ((_b = props.renderOption) === null || _b === void 0 ? void 0 : _b.call(props, option)) || option.label;
    }, [props, inputValue]);
    const handleDeleteOption = react_1.useCallback((option) => {
        var _a;
        if (props.multiple) {
            const tempData = inputValue.filter((o) => o.value !== option.value);
            setInputValue(tempData);
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, tempData);
        }
    }, [inputValue, setInputValue, props]);
    const testId = props['data-testid'] || (props.multiple ? 'multiselect' : 'autosuggest');
    return (react_1.default.createElement(Stack_1.default, null,
        react_1.default.createElement(Autocomplete_1.default, { multiple: props.multiple, "data-testid": testId, disabled: disabled, autoHighlight: true, popupIcon: null, freeSolo: freeSolo, disableClearable: disableClearable, open: statusType === 'error' || statusType === 'loading' ? true : open, id: controlId, value: inputValue, noOptionsText: empty, options: flattenOptions, groupBy: (option) => option.group || '', loadingText: loadingAndErrorText, getOptionSelected: getOptionsSelected, onChange: handleOnChange, onInputChange: handleOnInput, onFocus: onFocus, onBlur: onBlur, onOpen: () => {
                setOpen(true);
            }, onClose: () => {
                setOpen(false);
            }, loading: statusType !== 'finished', renderOption: renderOption, filterOptions: filterOptions, getOptionLabel: (option) => option.label || '', renderInput: textfield }),
        props.multiple && (react_1.default.createElement(TokenGroup_1.default, { items: (inputValue || []), onDismiss: handleDeleteOption }))));
};
exports.AutosuggestBase = AutosuggestBase;
const Autosuggest = (props) => {
    return react_1.default.createElement(AutosuggestBase, Object.assign({ multiple: false, checkboxes: false }, props));
};
exports.default = Autosuggest;
//# sourceMappingURL=index.js.map